'use client'
import { cn } from '@/utilities/ui'
import useClickableCard from '@/utilities/useClickableCard'
import { useLocale, useTranslations } from 'next-intl'
import React, { Fragment } from 'react'

import { LocaleLink } from '@/components/LocaleLink'

import type { Post } from '@/payload-types'
import { formatEditorialDate } from '@/modules/languages/lib/dateLocale'
import { Media } from '@/components/Media'
import { ThumbnailFallback } from '@/components/Media/ThumbnailFallback'
import {
  buildPostUrl,
  extractPrimaryCategorySlug,
  type PostUrlStructure,
} from '@/utilities/resolvePostPattern'
import { getPostListThumbnail } from '@/utilities/postListThumbnail'
import { buildAuthorProfilePath } from '@/utilities/authorProfilePaths'
import { buildCategoryArchivePath } from '@/utilities/taxonomyArchivePaths'
import { useTaxonomyArchivePaths } from '@/providers/TaxonomyArchivePathsContext'

export type CardPostData = Pick<
  Post,
  | 'slug'
  | 'categories'
  | 'tags'
  | 'meta'
  | 'heroImage'
  | 'title'
  | 'subtitle'
  | 'publishedAt'
  | 'readingTimeMinutes'
  | 'populatedAuthors'
>

export const Card: React.FC<{
  alignItems?: 'center'
  className?: string
  doc?: CardPostData
  relationTo?: 'posts'
  showCategories?: boolean
  /** Liga cada categoria ao arquivo `/categoria/[slug]`. */
  linkCategories?: boolean
  title?: string
  /** Prefixo de URL resolvido pelo Server Component pai (ex.: 'noticias'). */
  urlPrefix?: string
  /** Estrutura de URL configurada no CMS. */
  urlStructure?: PostUrlStructure | string
  /** Autor, data e tempo de leitura (quando os campos existem no doc). */
  showAuthorMeta?: boolean
  /** Prioridade de carregamento da imagem (ex.: destaque). */
  imagePriority?: boolean
}> = (props) => {
  const t = useTranslations('Blog')
  const locale = useLocale()
  const { showCategoryBaseInSlug, categoryUrlBase } = useTaxonomyArchivePaths()
  const { cardRef, linkRef } = useClickableCard({})
  const {
    className,
    doc,
    relationTo,
    showCategories,
    linkCategories = false,
    title: titleFromProps,
    urlPrefix,
    urlStructure,
    showAuthorMeta = false,
    imagePriority = false,
  } = props

  const {
    slug,
    categories,
    meta,
    title,
    subtitle,
    publishedAt,
    readingTimeMinutes,
    populatedAuthors,
  } = doc || {}
  const { description } = meta || {}
  const listImage = doc ? getPostListThumbnail(doc) : null

  const hasCategories = categories && Array.isArray(categories) && categories.length > 0
  const titleToUse = titleFromProps || title
  const excerpt = (subtitle?.trim() || description?.trim() || '').replace(/\s/g, ' ')
  const primaryAuthor = populatedAuthors?.[0]
  const publishedLabel =
    publishedAt &&
    formatEditorialDate(publishedAt, locale, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    })

  const href =
    relationTo === 'posts' && slug
      ? buildPostUrl(
          urlStructure as PostUrlStructure | null,
          slug,
          urlPrefix || 'posts',
          extractPrimaryCategorySlug(categories),
        )
      : `/${relationTo}/${slug}`

  const categoryArchiveHref = (catSlug: string) =>
    buildCategoryArchivePath(catSlug, showCategoryBaseInSlug, categoryUrlBase)

  return (
    <article
      className={cn(
        'border border-border rounded-lg overflow-hidden bg-card hover:cursor-pointer',
        className,
      )}
      ref={cardRef}
    >
      <div className="relative aspect-video w-full overflow-hidden bg-muted">
        {listImage ? (
          <Media
            fill
            htmlElement={null}
            priority={imagePriority}
            resource={listImage}
            size="33vw"
            pictureClassName="absolute inset-0 block size-full"
            imgClassName="size-full object-cover object-center"
          />
        ) : (
          <ThumbnailFallback sizes="33vw" imgClassName="size-full" />
        )}
      </div>
      <div className="p-4">
        {showCategories && hasCategories && (
          <div className="mb-4 flex flex-wrap gap-x-2 gap-y-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
            {categories?.map((category, index) => {
              if (typeof category === 'object' && category !== null && 'title' in category) {
                const categoryBadge =
                  'badge' in category && typeof category.badge === 'string' ? category.badge.trim() : ''
                const categoryTitle = categoryBadge || category.title || t('categoryFallback')
                const catSlug =
                  'slug' in category && typeof category.slug === 'string' ? category.slug : null
                const isLast = index === (categories?.length ?? 0) - 1

                return (
                  <Fragment key={category.id ?? index}>
                    {linkCategories && catSlug ? (
                      <LocaleLink
                        className="text-foreground/70 hover:text-foreground hover:underline"
                        href={categoryArchiveHref(catSlug)}
                      >
                        {categoryTitle}
                      </LocaleLink>
                    ) : (
                      <span>{categoryTitle}</span>
                    )}
                    {!isLast ? <span aria-hidden="true">,</span> : null}
                  </Fragment>
                )
              }

              return null
            })}
          </div>
        )}
        {titleToUse && (
          <div className="prose dark:prose-invert">
            <h3 className="text-lg font-semibold md:text-xl">
              <LocaleLink
                className="not-prose text-foreground hover:underline"
                href={href}
                ref={linkRef}
              >
                {titleToUse}
              </LocaleLink>
            </h3>
          </div>
        )}
        {excerpt ? (
          <p className="text-muted-foreground mt-2 line-clamp-3 text-sm leading-relaxed">
            {excerpt}
          </p>
        ) : null}
        {showAuthorMeta ? (
          <div className="text-muted-foreground mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
            {primaryAuthor?.name && primaryAuthor.id ? (
              <>
                <LocaleLink
                  className="font-medium text-foreground/80 hover:text-foreground hover:underline"
                  // SEO-3 (auditoria SEO 14/09) — S16: sem barra final (`/posts/user/1/` era 308).
                  href={buildAuthorProfilePath(primaryAuthor.id)}
                  rel="author"
                >
                  {t('byAuthor', { name: primaryAuthor.name })}
                </LocaleLink>
                {(publishedLabel || readingTimeMinutes != null) && (
                  <span aria-hidden="true" className="text-muted-foreground">
                    •
                  </span>
                )}
              </>
            ) : null}
            {publishedLabel ? (
              <time dateTime={publishedAt ?? undefined}>{publishedLabel}</time>
            ) : null}
            {readingTimeMinutes != null && readingTimeMinutes > 0 ? (
              <>
                {publishedLabel ? (
                  <span aria-hidden="true" className="text-muted-foreground">
                    •
                  </span>
                ) : null}
                <span>{t('readingTime', { minutes: readingTimeMinutes })}</span>
              </>
            ) : null}
          </div>
        ) : null}
      </div>
    </article>
  )
}
